import CheckIcon from '@mui/icons-material/Check';
import { FormLabel, Stack, TextField, ToggleButton } from '@mui/material';
import type { AgenticPlatformAskUserField } from 'agentic-platform-schema';
import { useId } from 'react';

import { FormField, Text, isDefined } from 'common';

interface AgenticChatQuestionFieldProps {
  answer: string[];
  disabled: boolean;
  field: AgenticPlatformAskUserField;
  onAnswerChange: (answer: string[]) => void;
  /** Called when a choice answers the whole question on its own, so there is nothing left to fill in. */
  onAnswerCommitted?: (answer: string[]) => void;
}

/** One answer the agent asked for, rendered from the kind it asked with. */
export function AgenticChatQuestionField(props: AgenticChatQuestionFieldProps) {
  const { answer, disabled, field, onAnswerChange, onAnswerCommitted } = props;
  const fieldId = useId();

  if (field.kind === 'shortText' || field.kind === 'longText') {
    return (
      <FormField id={fieldId} label={field.label}>
        <TextField
          id={fieldId}
          placeholder={field.placeholder}
          disabled={disabled}
          multiline={field.kind === 'longText'}
          minRows={field.kind === 'longText' ? 3 : undefined}
          value={answer.at(0) ?? ''}
          onChange={(event) => onAnswerChange([event.target.value])}
          onKeyDown={(event) => {
            // A multiline field owes the user their newline, so it asks for a modifier.
            if (
              field.kind === 'longText' &&
              event.key === 'Enter' &&
              (event.metaKey || event.ctrlKey)
            ) {
              event.currentTarget.closest('form')?.requestSubmit();
            }
          }}
          size="small"
          fullWidth
        />
      </FormField>
    );
  }

  return (
    <Stack gap={0.5}>
      <FormLabel component="span" id={`${fieldId}-label`}>
        {field.label}
      </FormLabel>
      {/* Standalone buttons: the theme styles a grouped one as a cell of a horizontal row. */}
      <Stack gap={1} role="group" aria-labelledby={`${fieldId}-label`}>
        {field.choices.map((choice) => (
          <ToggleButton
            key={choice.value}
            value={choice.value}
            disabled={disabled}
            selected={answer.includes(choice.value)}
            onClick={() => {
              const nextAnswer =
                field.kind === 'singleChoice'
                  ? [choice.value]
                  : answer.includes(choice.value)
                    ? answer.filter((value) => value !== choice.value)
                    : [...answer, choice.value];
              onAnswerChange(nextAnswer);
              if (field.kind === 'singleChoice') {
                onAnswerCommitted?.(nextAnswer);
              }
            }}
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 1,
              textAlign: 'left',
              textTransform: 'none',
            }}
          >
            <Stack flex={1}>
              <Text variant="body2" bold>
                {choice.label}
              </Text>
              {isDefined(choice.description) ? (
                <Text variant="caption" color="text.secondary">
                  {choice.description}
                </Text>
              ) : null}
            </Stack>
            {answer.includes(choice.value) ? <CheckIcon color="primary" fontSize="small" /> : null}
          </ToggleButton>
        ))}
      </Stack>
    </Stack>
  );
}
